import {
  attachUnitToResult,
  buildFunctionDefSubstitutions,
  buildSubstitutions,
  buildSympyCode,
  buildSystemSolveSympyCode,
  collectUnitInlineChildren,
  collectVariableUnits,
  detectTrigFlags,
  encodeDecoratedName,
  extractUnits,
  getLabeledVariableName,
  getScalarRatioDivisor,
  hasUnknownUnitVariable,
  intervalGroupToSympy,
  normalizeResult,
  OPERATOR_SYMBOL_TO_SYMPY_RELATION_MAP,
  parseNodeToSympy,
  parseSympyResult,
  semanticEquals,
  semanticToSympyCode,
  SET_SYMBOL_TO_SYMPY_MAP,
  SOLVABLE_FUNCTION_KEYS,
  UNIT_CONFLICT,
  unitInlineChildrenToSemantic,
} from "@corca/sympy";

import { pyodideManager } from "@/components/pyodide/PyodideManager";
import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";
import { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import { OperatorSymbolNodeModel } from "@/models/nodes/OperatorSymbolNodeModel";
import { PlainTextNodeModel } from "@/models/nodes/PlainTextNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import {
  isEqualsSign,
  isFraction,
  isFractionDerivative,
  isPowerOperator,
  isRelation,
} from "@/models/utils/semanticChecks";
import { createLogger } from "@/utils/logger";
import { reportUnexpectedSympyError, trySympy } from "@/utils/sympyErrors";

import { applySequenceEllipsis, tryBuildSequenceHint } from "./getSequenceHint";
import { getTableCellFormulaContext, isFormulaRowHeader } from "./getTableCellFormula";
import { nodeToExpressionText } from "./nodeToExpressionText";
import {
  countRelations,
  documentScope,
  hasNonEqualRelation,
  makeEqualsRelationJson,
  scopeExpressionJson,
  scopeExpressionNodes,
  wrapperScope,
} from "./scopeExpressions";
import { substituteReferenceNodes } from "./substituteReferenceNodes";

import type { ScopeExpression } from "./scopeExpressions";
import type { HintExecution, PriorAssignment, PriorDomain, PriorFunctionDef } from "./types";
import type { PhantomHint } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import type { ClosingSymbol, NodeJson, OpeningSymbol } from "@corca/data";
import type { SympyCommand, SympyResult, UnitResult } from "@corca/sympy";
import type { Editor } from "@tiptap/core";

// TODO: switch from NodeModel to NodeJson and move these functions to @corca/sympy.
// This code should be mostly agnostic to node tree model.
// @see https://linear.app/corca-research/issue/D-3262

const EAGER_EVALUATION_FUNCTIONS = new Set([
  "function-greatest-common-divisor",
  "function-least-common-multiple",
  // Add others as discovered (e.g., 'operator-factorial' for large numbers)
]);

const INEQUALITY_OP_KEYS = new Set(
  Object.keys(OPERATOR_SYMBOL_TO_SYMPY_RELATION_MAP).filter((k) => k !== "symbol-equal"),
);

const SOLUTION_SET_TRIGGER_SYMBOL = "∈";

const logger = createLogger("PhantomHints");

/** Wrap a resolved execution as a lazily-evaluated PhantomHint (units/matrix/equals applied here). */
export function executionToPhantomHint(solver: HintExecution): PhantomHint {
  const hint = {
    label: solver.label,
    expressionText: solver.expressionText,
    usedWrapperIds: solver.usedWrapperIds,
    getHint: async () => {
      const res = await executeCode(solver.pythonCode, solver.symbolNodes, solver.inputHasDecimal);
      if (!res) return null;

      if (solver.matrixBrackets) {
        applyMatrixBrackets(res.json, solver.matrixBrackets);
      }

      if (solver.inputUnitResult) {
        res.json = attachUnitToResult(res.json, solver.inputUnitResult);
      }

      applySequenceEllipsis(res.json, solver);

      const equals =
        !solver.hasEagerFunction &&
        !solver.hasMatrixResult &&
        solver.inputSemantic !== undefined &&
        semanticEquals(solver.inputSemantic, res.outputSemantic);

      return { json: res.json, equals };
    },
  };

  return solver.command === "sequence"
    ? { ...hint, type: "sequence" }
    : { ...hint, type: "sympy", command: solver.command };
}

export function getPhantomHints(doc: DocumentModel, triggerSymbol?: string): PhantomHint[] {
  if (!pyodideManager.isReady) return [];

  const { tiptap } = doc.editor;
  if (!tiptap) return [];

  return getHintExecutions(doc, tiptap, triggerSymbol).map(executionToPhantomHint);
}

/**
 * Derive a variable's unit text from prior `name = value<unit>` assignments in the document.
 * Returns `null` if no assignment with a unit is found for `variableName`.
 */
export function getDerivedUnitText(doc: DocumentModel, variableName: string): string | null {
  const priorAssignments = collectPriorAssignments(wrapperScope(doc.textNode.wrapperNodes));
  const varUnits = collectVariableUnits(priorAssignments);
  return varUnits.get(variableName)?.text ?? null;
}

export function getHintExecutions(
  doc: DocumentModel,
  tiptap: Editor,
  triggerSymbol?: string,
): HintExecution[] {
  // Cursor in an empty table cell whose row header is a formula: evaluate that formula for the
  // current column, binding each variable to the value in its own row at the same column.
  const tableFormula = getTableCellFormulaContext(doc, tiptap);
  if (tableFormula) {
    return buildHintExecutions(tableFormula.expression, 0, tableFormula.scope, false);
  }

  if (!doc.cursor.isActive) {
    // TODO: find prev. expression
    return [];
  }

  // ignore for nested nodes
  if (doc.cursor.node?.ancestors.some((n) => n.is(InlineNodeModel))) {
    return [];
  }

  const nodesBefore = [...doc.cursor.nodesBeforeCursor];
  let startIndex = 0;

  // `=` slices the expression to its RHS; inequalities keep the full LHS-op-RHS.
  for (let i = 0; i < nodesBefore.length; i++) {
    const n = nodesBefore[i];
    if (isRelation(n)) {
      if (n.value === "=") {
        startIndex = nodesBefore.length - i;
      }
      break;
    }
  }

  const triggerIsSolutionSet = triggerSymbol === SOLUTION_SET_TRIGGER_SYMBOL;

  // Reverse to get correct order and slice to get expression after relation operator
  const reversedNodes = [...nodesBefore].reverse();
  const expression = reversedNodes.slice(startIndex);

  const pos = doc.cursor.parentTiptapNode.position;

  return buildHintExecutions(
    expression,
    startIndex,
    documentScope(doc, tiptap, pos),
    triggerIsSolutionSet,
  );
}

/**
 * Build executions for a computed wrapper: evaluates the expression before the
 * first top-level "=" against the preceding wrappers in scope.
 * Shares the whole pipeline with cursor-based hints (command detection, context
 * collection, substitutions, units).
 */
export function getComputedExecutions(
  doc: DocumentModel,
  wrapper: WrapperNodeModel,
  precedingWrapperIds: string[],
): HintExecution[] {
  const inline = wrapper.nodes.find((n) => n.is(InlineNodeModel));
  if (!inline) return [];

  const { nodes } = inline;
  const eqIndex = nodes.findIndex(isEqualsSign);
  const expression = eqIndex === -1 ? [...nodes] : nodes.slice(0, eqIndex);

  if (expression.length === 0) return [];

  return buildHintExecutions(
    expression,
    0,
    wrapperScope(doc.textNode.wrapperNodes, precedingWrapperIds),
    false,
  );
}

/**
 * @param scope Lines in document order; later entries shadow earlier ones. Cursor hints and table
 *   cells differ only in how they build it, so every context lookup below is shared.
 */
export function buildHintExecutions(
  initialExpression: NodeModel[],
  startIndex: number,
  scope: ScopeExpression[],
  triggerIsSolutionSet: boolean,
): HintExecution[] {
  let expression = initialExpression;

  const earlyFunctionDefs = collectPriorFunctionDefs(scope);
  const definedFuncNames = new Set<string>(earlyFunctionDefs.map((fd) => fd.funcName));

  const earlyPriorAssignments = collectPriorAssignments(scope);
  const matrixBoundVarNames = new Set<string>(
    earlyPriorAssignments
      .filter((a) => a.rhsNodes.some(containsMatrixJson))
      .map((a) => a.variableName),
  );

  const { commands, solveVars } = analyzeExpression(
    expression,
    startIndex,
    definedFuncNames,
    matrixBoundVarNames,
    triggerIsSolutionSet,
  );

  if (commands.length === 0) return [];

  // Multi-variable system solve (e.g., "x, y =" after two equations)
  if (solveVars && solveVars.length >= 2) {
    return buildSystemSolveHints(solveVars, scope, earlyFunctionDefs);
  }

  const solveVar = solveVars?.[0] ?? null;

  // Tried before the generic solve path, which can't match an indexed def (labeledValue "a_k", not "a").
  if (
    solveVar &&
    !triggerIsSolutionSet &&
    expression.length === 1 &&
    expression[0].is(VariableNodeModel) &&
    !expression[0].hasParams
  ) {
    const sequenceHint = tryBuildSequenceHint(solveVar, scope);
    if (sequenceHint) return [sequenceHint];
  }

  // Encode the solve variable with decorators for SymPy
  let encodedSolveVar: string | null = null;
  let solvedEquasionWrapperId: string | null = null;

  if (solveVar && expression.length === 1 && expression[0].is(VariableNodeModel)) {
    const varNode = expression[0];
    const decoratorNames =
      varNode.symbolNode?.decorators.map((d) => d.props.name).filter(Boolean) ?? [];
    encodedSolveVar = encodeDecoratedName(solveVar, decoratorNames as string[]);
  }

  // A name used bare (`v`) but defined as a function (`v(t) = ...`) resolves to its body.
  const bareFuncCandidates = new Set<string>();
  for (const node of expression) {
    findDescendant((n) => {
      if (n.is(VariableNodeModel) && !n.hasParams) {
        const name = n.symbolNode?.labeledValue ?? "";
        if (definedFuncNames.has(name)) bareFuncCandidates.add(name);
      }
      return false;
    }, node);
  }

  const wrapperPos = (id: string) => scope.findIndex((e) => e.wrapperId === id);
  const funcDefSubstitutions: PriorAssignment[] = [];
  const unresolvableNames = new Set<string>();
  // Bare use means `v` is a value, not a callable, so its Lambda definition must not emit.
  const bareUsedFuncDefWrapperIds = new Set<string>();
  for (const fd of earlyFunctionDefs) {
    if (!bareFuncCandidates.has(fd.funcName)) continue;
    bareUsedFuncDefWrapperIds.add(fd.wrapperId);
    // A later scalar assignment redefines the name; its substitution already applies.
    const scalar = earlyPriorAssignments.find((a) => a.variableName === fd.funcName);
    if (scalar && wrapperPos(scalar.wrapperId) > wrapperPos(fd.wrapperId)) continue;
    // Self-reference or a reference to another defined function has no safe substitution.
    const bodyNames = new Set<string>();
    fd.rhsNodes.forEach((n) => collectBareVariableNames(n, bodyNames));
    if (bodyNames.has(fd.funcName) || [...bodyNames].some((b) => definedFuncNames.has(b))) {
      unresolvableNames.add(fd.funcName);
      continue;
    }
    funcDefSubstitutions.push({
      variableName: fd.funcName,
      rhsNodes: fd.rhsNodes,
      wrapperId: fd.wrapperId,
    });
  }

  if (solveVar && unresolvableNames.has(solveVar)) return [];

  // A bare solve target `v` defined as `v(t) = body` solves `Eq(v, body)` (args stripped),
  // mirroring a scalar assignment. Other (nested) uses are substituted below.
  const solveTargetDef =
    solveVar && expression.length === 1 && expression[0].is(VariableNodeModel)
      ? funcDefSubstitutions.find((s) => s.variableName === solveVar)
      : undefined;
  let solveTargetEquation: NodeJson | null = null;
  if (solveTargetDef) {
    const bareVar: NodeJson = {
      type: "variable",
      props: {},
      children: [{ type: "symbol", props: { value: solveVar! }, children: [] }],
    };
    solveTargetEquation = {
      type: "inline",
      props: {},
      children: [bareVar, makeEqualsRelationJson(), ...solveTargetDef.rhsNodes],
    };
    solvedEquasionWrapperId = solveTargetDef.wrapperId;
    // The body is the equation RHS now, so do not also substitute it.
    funcDefSubstitutions.splice(funcDefSubstitutions.indexOf(solveTargetDef), 1);
  }

  let isMatrixAssignment = false;
  // A synthesized row equation has no `=` node, so its JSON cannot be rebuilt from `expression`.
  let equationJson: NodeJson[] | null = null;

  // If analyzeExpression resolved a preceding equation/relational for solve, use that
  if (solveVar && !solveTargetDef) {
    const precedingLines = triggerIsSolutionSet
      ? findPrecedingRelational(scope, [solveVar])
      : findPrecedingEquasion(scope, [solveVar]);
    const [line] = precedingLines;
    // Nothing in scope to solve against.
    if (!line) return [];

    expression = scopeExpressionNodes(line);
    equationJson = scopeExpressionJson(line);
    solvedEquasionWrapperId = line.wrapperId;

    // Matrix assignment (e.g., `c = a · b`): sp.solve(Eq(Symbol, Matrix), Symbol)
    // fails with TypeError. Strip LHS+"=", evaluate RHS only via .doit() path.
    if (line.lhs) {
      const rhsNodes = line.rhs;
      const rhsJson = rhsNodes.map((n) => n.toJSON());
      const rhsHasMatrix = rhsJson.some(containsMatrixJson);
      const rhsRefsMatrixBoundVar = rhsNodes.some((n) =>
        findDescendant(
          (d) =>
            d.is(VariableNodeModel) && matrixBoundVarNames.has(d.symbolNode?.labeledValue ?? ""),
          n,
        ),
      );
      if (rhsHasMatrix || rhsRefsMatrixBoundVar) {
        expression = rhsNodes;
        equationJson = null;
        encodedSolveVar = null;
        isMatrixAssignment = true;
      }
    }
  }

  const inlineNode: NodeJson = solveTargetEquation ?? {
    type: "inline",
    props: {},
    children: equationJson ?? expression.map((n) => substituteReferenceNodes(n.toJSON())),
  };

  const expressionText = nodeToExpressionText(inlineNode, { normalizeExp: true });

  const parsed = trySympy(() => parseNodeToSympy(inlineNode));
  if ("error" in parsed) {
    logger.warn(
      "Failed to parse expression for hints:",
      parsed.error,
      "\nExpression:",
      expressionText,
    );
    return [];
  }

  parsed.warnings.forEach((warning) => logger.warn(warning, "\nExpression:", expressionText));

  if (!parsed.expr) {
    logger.warn("Expression parsed to empty string:\n", expressionText);
    return [];
  }

  // The line being solved must not also substitute itself away.
  const assignmentScope = solvedEquasionWrapperId
    ? scope.filter((e) => e.wrapperId !== solvedEquasionWrapperId)
    : scope;
  const priorAssignments = collectPriorAssignments(assignmentScope);
  const priorDomains = collectPriorDomains(assignmentScope);

  priorAssignments.push(...funcDefSubstitutions);

  // Collect unit inline nodes from scope to decide SI vs non-SI path
  const scopeUnitInlines: NodeJson[][] = [...collectUnitInlineChildren(inlineNode)];
  for (const { rhsNodes } of priorAssignments) {
    const rhsInline: NodeJson = { type: "inline", props: {}, children: rhsNodes };
    scopeUnitInlines.push(...collectUnitInlineChildren(rhsInline));
  }

  let solvedAssignmentRhsNodes: NodeJson[] | null = null;
  if (solvedEquasionWrapperId) {
    const solvedLine = scope.find((e) => e.wrapperId === solvedEquasionWrapperId);
    const solvedAssignment = solvedLine ? tryParseAssignment(solvedLine) : null;
    if (solvedAssignment) {
      solvedAssignmentRhsNodes = solvedAssignment.rhsNodes;
      const solvedRhsInline: NodeJson = {
        type: "inline",
        props: {},
        children: solvedAssignment.rhsNodes,
      };
      scopeUnitInlines.push(...collectUnitInlineChildren(solvedRhsInline));
    }
  }

  const allScopeUnitsAreSi =
    scopeUnitInlines.length === 0 ||
    scopeUnitInlines.every((children) => unitInlineChildrenToSemantic(children) !== null);

  let inputUnitResult: UnitResult | null = null;

  /** When non-SI units are present, force non-SI path for all subsequent parseNodeToSympy calls. */
  const emitSiUnitsOverride: boolean | null = allScopeUnitsAreSi ? null : false;

  if (scopeUnitInlines.length > 0) {
    const varUnits = collectVariableUnits(priorAssignments);
    let solvedEquationUnit: UnitResult | null = null;
    if (solvedAssignmentRhsNodes) {
      const solvedRhsInline: NodeJson = {
        type: "inline",
        props: {},
        children: solvedAssignmentRhsNodes,
      };
      const solvedUnit = extractUnits(solvedRhsInline, varUnits);
      if (solvedUnit && solvedUnit !== UNIT_CONFLICT) {
        solvedEquationUnit = solvedUnit;
      }
    }

    const unitExtractionResult = solvedEquationUnit ?? extractUnits(inlineNode, varUnits);
    if (unitExtractionResult === UNIT_CONFLICT && !hasUnknownUnitVariable(inlineNode, varUnits)) {
      return [];
    }
    if (!allScopeUnitsAreSi && unitExtractionResult !== UNIT_CONFLICT) {
      inputUnitResult = unitExtractionResult;
    }
  }

  const scalarRatioDivisor = inputUnitResult ? getScalarRatioDivisor(inputUnitResult.text) : null;

  // Bare-used defs go through buildSubstitutions or a scalar assignment, not the Lambda path.
  const excludedFuncDefWrapperIds = new Set(bareUsedFuncDefWrapperIds);
  if (solvedEquasionWrapperId) excludedFuncDefWrapperIds.add(solvedEquasionWrapperId);
  const priorFunctionDefs = earlyFunctionDefs.filter(
    (fd) => !excludedFuncDefWrapperIds.has(fd.wrapperId),
  );
  // Build function defs first so body variables (e.g., "a" in f(x) = x + a) are
  // discovered before buildSubstitutions, which needs them to resolve a = 2.
  let functionDefs;
  let substitutions;
  let usedWrapperIds;
  let substitutionsHaveDecimal;
  try {
    const defs = buildFunctionDefSubstitutions(
      priorFunctionDefs,
      parsed.variables,
      emitSiUnitsOverride,
    );
    const subs = buildSubstitutions(priorAssignments, parsed.variables, emitSiUnitsOverride);

    ({ functionDefs } = defs);
    ({ substitutions, usedWrapperIds, hasDecimal: substitutionsHaveDecimal } = subs);
    [...defs.warnings, ...subs.warnings].forEach((warning) => logger.warn(warning));
  } catch (error) {
    reportUnexpectedSympyError(error);
    logger.warn("Failed to build substitutions for hints:", error, "\nExpression:", expressionText);
    return [];
  }

  for (const [funcName, funcDef] of functionDefs.entries()) {
    if (parsed.variables.has(funcName)) {
      usedWrapperIds.add(funcDef.wrapperId);
    }
  }

  // Check if expression contains eager-evaluation functions (gcd, lcm, etc.)
  const hasEagerFunction = expression.some(
    (node) =>
      (node.is(FunctionNodeModel) || node.is(OperatorNodeModel)) &&
      EAGER_EVALUATION_FUNCTIONS.has(node.props.key ?? ""),
  );

  // Check if the result will be a matrix — either directly in the expression
  // or via substituted variables that resolve to matrices.
  const isMatrixNode = (n: NodeModel) => n.is(MatrixNodeModel) && n.rows * n.cols > 1;
  const expressionHasMatrix = expression.some((node) => !!findDescendant(isMatrixNode, node));
  const substitutedVarsHaveMatrix = priorAssignments.some((a) =>
    a.rhsNodes.some((n) => containsMatrixJson(n)),
  );
  const hasMatrixResult = expressionHasMatrix || substitutedVarsHaveMatrix;

  // Extract matrix bracket style from input expression to preserve in output
  const matrixBrackets = findInputMatrixBrackets(expression);

  const trigFlags = detectTrigFlags(expression.map((node) => node.toJSON()));

  // Decimals typed in the expression or in a substituted-in prior assignment trigger decimal
  // output rendering. Sourced from parseNodeToSympy's `hasDecimal` meta (buildSubstitutions only
  // flags assignments it actually resolves, so an unrelated earlier `z = 0.3` doesn't poison it).
  const inputHasDecimal = parsed.hasDecimal || substitutionsHaveDecimal;

  return commands
    .map((command) => {
      const effectiveCommand = isMatrixAssignment && command === "solve" ? "evaluate" : command;
      const label = command.charAt(0).toUpperCase() + command.slice(1);

      let solveDomain: string | undefined;
      if (command === "solve" && solveVar) {
        const domains = priorDomains.get(solveVar);
        if (domains) {
          const resolved = resolveDomain(domains, parsed.variables);
          if (resolved) {
            solveDomain = resolved.sympy;
            for (const id of resolved.wrapperIds) usedWrapperIds.add(id);
          }
        }
      }

      const code = trySympy(() =>
        buildSympyCode(effectiveCommand, parsed.expr, parsed.variables, {
          solveVariable: effectiveCommand === "solve" ? (encodedSolveVar ?? undefined) : undefined,
          substitutions,
          functionDefs,
          indexedBases: parsed.indexedBases,
          trigFlags,
          domain: solveDomain,
          scalarRatioDivisor,
        }),
      );

      if ("error" in code) {
        logger.warn(
          "Failed to build solve code for hints:",
          code.error,
          "\nExpression:",
          expressionText,
        );
        return null;
      }

      return {
        label,
        command,
        pythonCode: code.expr,
        expressionText,
        hasEagerFunction,
        hasMatrixResult,
        usedWrapperIds: Array.from(usedWrapperIds),
        matrixBrackets,
        inputUnitResult,
        symbolNodes: parsed.symbolNodes,
        inputSemantic: parsed.semantic,
        inputHasDecimal,
      };
    })
    .filter(Boolean);
}

/**
 * Build hints for solving a system of equations with multiple variables.
 * Finds all preceding equations mentioning any of the target variables,
 * parses them, and generates a single sp.solve([eq1, eq2, ...], [x, y, ...]) call.
 */
function buildSystemSolveHints(
  solveVars: string[],
  scope: ScopeExpression[],
  _earlyFunctionDefs: PriorFunctionDef[],
): {
  label: string;
  command: SympyCommand;
  pythonCode: string | null;
  expressionText: string;
  hasEagerFunction: boolean;
  hasMatrixResult: boolean;
  usedWrapperIds?: string[];
  matrixBrackets?: undefined;
  inputUnitResult?: null;
  inputHasDecimal?: boolean;
}[] {
  const precedingEquations = findPrecedingEquasion(scope, solveVars);

  if (precedingEquations.length === 0) return [];

  const equationWrapperIds = new Set(precedingEquations.map((eq) => eq.wrapperId));

  // Collect assignments that are NOT part of the equations (for substitutions)
  const priorAssignments = collectPriorAssignments(
    scope.filter((e) => !equationWrapperIds.has(e.wrapperId)),
  );

  // Parse all equations into SymPy expressions using a shared variable namespace
  const sharedVariables = new Map<string, string>();
  const parsedEquations: string[] = [];
  const usedWrapperIds = new Set<string>();
  let inputHasDecimal = false;

  for (const eq of precedingEquations) {
    const eqInline: NodeJson = { type: "inline", props: {}, children: scopeExpressionJson(eq) };
    // An equation that does not translate simply doesn't join the system.
    const parsed = trySympy(() => parseNodeToSympy(eqInline, sharedVariables));
    if ("error" in parsed) continue;
    parsed.warnings.forEach((warning) => logger.warn(warning));
    if (!parsed.expr) continue;
    if (parsed.hasDecimal) inputHasDecimal = true;
    parsedEquations.push(parsed.expr);
    usedWrapperIds.add(eq.wrapperId);
  }

  if (parsedEquations.length === 0) return [];

  // Build substitutions from prior assignments
  const subs = trySympy(() => buildSubstitutions(priorAssignments, sharedVariables));
  if ("error" in subs) return [];
  const { substitutions, hasDecimal: substitutionsHaveDecimal } = subs;
  subs.warnings.forEach((warning) => logger.warn(warning));
  for (const id of subs.usedWrapperIds) usedWrapperIds.add(id);

  // A decimal in a substituted-in assignment also triggers decimal rendering.
  if (substitutionsHaveDecimal) inputHasDecimal = true;

  // Build the solve variables list — encode each with its symbol
  const solveVarSymbols: string[] = [];
  for (const varName of solveVars) {
    if (!sharedVariables.has(varName)) {
      sharedVariables.set(varName, `var${sharedVariables.size}`);
    }
    solveVarSymbols.push(sharedVariables.get(varName)!);
  }

  const code = buildSystemSolveSympyCode(
    parsedEquations,
    solveVarSymbols,
    sharedVariables,
    substitutions,
  );

  const expressionText = solveVars.join(", ");

  return [
    {
      label: "Solve",
      command: "solve",
      pythonCode: code,
      expressionText,
      hasEagerFunction: false,
      hasMatrixResult: false,
      usedWrapperIds: Array.from(usedWrapperIds),
      matrixBrackets: undefined,
      inputUnitResult: null,
      inputHasDecimal,
    },
  ];
}

/**
 * Generate Python code for solving a system of equations.
 */
/**
 * Extract variable names from a parenthesized tuple group like (x, y).
 * Structure: group( inline( var(x), opsym(,), var(y) ) )
 * The inline contains alternating variables and comma operator symbols.
 * Returns null if the structure doesn't match.
 */
function extractTupleVariables(group: GroupNodeModel): string[] | null {
  // Find the inline node inside the group (may be wrapped in a matrix)
  let inline: InlineNodeModel | null = null;
  for (const child of group.childrenPlease) {
    if (child.is(InlineNodeModel)) {
      inline = child;
      break;
    }
    if (child.is(MatrixNodeModel)) {
      // 1×1 matrix wrapping the inline
      for (const matrixChild of child.childrenPlease) {
        if (matrixChild.is(InlineNodeModel)) {
          inline = matrixChild;
          break;
        }
      }
      break;
    }
  }
  if (!inline) return null;

  const { nodes } = inline;
  // Must be: var, comma, var [, comma, var ...]  — odd length, >= 3
  if (nodes.length < 3 || nodes.length % 2 === 0) return null;

  const varNames: string[] = [];
  for (let i = 0; i < nodes.length; i++) {
    if (i % 2 === 0) {
      const n = nodes[i];
      if (!n.is(VariableNodeModel) || n.hasParams) return null;
      const name = n.symbolNode?.labeledValue ?? null;
      if (!name) return null;
      varNames.push(name);
    } else {
      const n = nodes[i];
      if (!n.is(OperatorSymbolNodeModel) || n.value !== ",") return null;
    }
  }

  return varNames.length >= 2 ? varNames : null;
}

function findDescendant(
  predicate: (node: NodeModel) => boolean,
  node?: NodeModel,
): NodeModel | null {
  if (!node) return null;
  if (predicate(node)) return node;
  for (const child of node.childrenPlease) {
    const found = findDescendant(predicate, child);
    if (found) return found;
  }
  return null;
}

/**
 * True when the subtree rooted at `node` contains only numeric literals and
 * arithmetic structure: no variables, constants (pi, e, etc.), units, or matrices.
 * Matrices are excluded because sp.N on a matrix trig arg is not a scalar numeric eval.
 */
function containsMatrixJson(node: NodeJson): boolean {
  if (node.type === "matrix" && (node.props.rows ?? 1) * (node.props.cols ?? 1) > 1) return true;
  return node.children.some(containsMatrixJson);
}

// Names of bare variables (no parenthesised args) appearing anywhere in a NodeJson tree.
function collectBareVariableNames(node: NodeJson, names: Set<string>): void {
  if (node.type === "variable" && !node.children.some((c) => c.type === "inline")) {
    const symbol = node.children.find((c) => c.type === "symbol");
    const value = symbol?.props.value;
    if (typeof value === "string") names.add(value);
  }
  for (const child of node.children) collectBareVariableNames(child, names);
}

function toInlineNode(nodes: NodeJson[]): NodeJson {
  return {
    type: "inline",
    props: {},
    children: nodes,
  } as const;
}

/**
 * Analyze an expression and return the ordered list of applicable hint commands.
 * Combines gating (what's applicable) and classification (what order) in a single pass.
 */
function analyzeExpression(
  expression: NodeModel[],
  startIndex: number,
  definedFuncNames?: Set<string>,
  matrixBoundVarNames?: Set<string>,
  triggerIsSolutionSet?: boolean,
): { commands: SympyCommand[]; solveVars: string[] | null } {
  const isMatrixOrMatrixVar = (n: NodeModel): boolean => {
    if (findDescendant((d) => d.is(MatrixNodeModel) && d.rows * d.cols > 1, n)) return true;
    if (
      matrixBoundVarNames &&
      n.is(VariableNodeModel) &&
      matrixBoundVarNames.has(n.symbolNode?.labeledValue ?? "")
    ) {
      return true;
    }
    return false;
  };
  // Parenthesized variable tuple — solve system of equations (e.g., "(x, y) =")
  if (startIndex === 0 && expression.length === 1 && expression[0].is(GroupNodeModel)) {
    const group = expression[0];
    if (group.openingSymbol === "(" && group.closingSymbol === ")") {
      const varNames = extractTupleVariables(group);
      if (varNames && varNames.length >= 2) {
        return { commands: ["solve"], solveVars: varNames };
      }
    }
  }

  // Single solvable node (integral, limit, summation, product, derivative)
  if (expression.length === 1) {
    const n = expression[0];

    if (n.is(FunctionNodeModel) && SOLVABLE_FUNCTION_KEYS.has(n.props.key ?? "")) {
      return { commands: ["solve"], solveVars: null };
    }

    if (n.is(OperatorNodeModel) && n.operatorType === "derivative") {
      return { commands: ["solve"], solveVars: null };
    }

    // Fraction-derivative form: d/dx (...) — needs .doit() to evaluate the Derivative.
    // Routes through "solve" so that solve_or_evaluate calls .doit() (see Gotcha 2 in apps/web/CLAUDE.md).
    if (isFractionDerivative(n)) {
      return { commands: ["solve"], solveVars: null };
    }

    if (n.is(MatrixNodeModel) && n.rows * n.cols > 1) {
      return { commands: ["solve"], solveVars: null };
    }

    // Single variable with params whose name is a defined function (e.g., f(3))
    if (
      n.is(VariableNodeModel) &&
      n.hasParams &&
      definedFuncNames?.has(n.symbolNode?.labeledValue ?? "")
    ) {
      return { commands: ["simplify"], solveVars: null };
    }

    // Single variable — try to solve a preceding equation for it
    if (startIndex === 0 && n.is(VariableNodeModel) && !n.hasParams) {
      const varName = n.symbolNode?.labeledValue ?? null;
      return { commands: ["solve"], solveVars: varName ? [varName] : null };
    }

    // `x ∈` — solve the preceding inequality for x.
    if (triggerIsSolutionSet && n.is(VariableNodeModel) && !n.hasParams) {
      const varName = n.symbolNode?.labeledValue ?? null;
      return { commands: ["solve"], solveVars: varName ? [varName] : null };
    }
  }

  // Inline inequalities produce no hint — cross-line `∈` is the only entry point.
  const inequalityOps = expression.filter(
    (n) => n.is(OperatorSymbolNodeModel) && INEQUALITY_OP_KEYS.has(n.symbolNode?.props?.key ?? ""),
  );
  const eqOps = expression.filter(isEqualsSign);
  if (inequalityOps.length + eqOps.length > 1) {
    return { commands: [], solveVars: null };
  }
  if (inequalityOps.length === 1) {
    return { commands: [], solveVars: null };
  }

  // Matrix with power operator (e.g. A^{-1}, A^2, A^⊺): needs .doit() to evaluate.
  // Also covers variables that resolve to matrices via prior assignments.
  if (expression.length === 2) {
    const hasMatrix = expression.some(isMatrixOrMatrixVar);
    const hasPower = expression.some(isPowerOperator);
    if (hasMatrix && hasPower) {
      return { commands: ["solve"], solveVars: null };
    }
  }

  // Trivial expression — no hints
  const isNonTrivial =
    expression.length > 1 ||
    (expression.length === 1 &&
      (expression[0].is(OperatorNodeModel) ||
        expression[0].is(GroupNodeModel) ||
        expression[0].is(FunctionNodeModel) ||
        (expression[0].is(MatrixNodeModel) && expression[0].rows * expression[0].cols > 1) ||
        // Number with a power child (e.g. 2^3) — the editor nests power inside the number node
        expression[0].childrenPlease.some(isPowerOperator)));

  if (!isNonTrivial) return { commands: [], solveVars: null };

  // Fractions prioritize apart
  const hasFraction = expression.some((node) =>
    findDescendant((n) => isFraction(n) && !isFractionDerivative(n), node),
  );

  if (hasFraction) {
    return { commands: ["apart", "factor", "evaluate", "simplify", "expand"], solveVars: null };
  }

  return { commands: ["evaluate", "simplify", "expand", "factor"], solveVars: null };
}

export async function executeCode(
  pythonCode: string | null,
  symbolNodes?: Map<string, NodeJson>,
  inputHasDecimal?: boolean,
): Promise<{ json: NodeJson; outputSemantic: SympyResult } | null> {
  if (!pythonCode) return null;
  try {
    const res = await pyodideManager.executeSympyCode(pythonCode);
    const { result } = res;
    if (!result) return null;
    const parsedRes = trySympy(() =>
      parseSympyResult(result, undefined, symbolNodes, inputHasDecimal),
    );
    if ("error" in parsedRes) {
      logger.warn("Failed to parse SymPy result for hints:", parsedRes.error);
      return null;
    }
    parsedRes.warnings.forEach((warning) => logger.warn(warning));
    if (parsedRes.nodes.length === 0) return null;
    return {
      json: toInlineNode(parsedRes.nodes),
      outputSemantic: normalizeResult(result),
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.groupCollapsed("SymPy execution error");
    logger.warn("Failed to execute SymPy code for hints:", e);
    // eslint-disable-next-line no-console
    console.groupEnd();
    return null;
  }
}

/**
 * Collect ALL simple variable = expression assignments from the lines in scope.
 * Last-write-wins for redefinitions.
 */
export function collectPriorAssignments(scope: ScopeExpression[]): PriorAssignment[] {
  const assignmentMap = new Map<string, PriorAssignment>();

  for (const expr of scope) {
    const assignment = tryParseAssignment(expr);
    if (assignment) {
      assignmentMap.set(assignment.variableName, assignment);
    }
  }

  return Array.from(assignmentMap.values());
}

/**
 * Parse a line as `variable = expression`. A chained or mixed relation is not a definition.
 *
 * For example, `a = b = c` and `a = b < c` are rejected.
 */
function tryParseDefinition(expr: ScopeExpression): {
  lhsNode: VariableNodeModel;
  lhsName: string;
  rhsNodes: NodeModel[];
  rhsJson: NodeJson[];
} | null {
  const { lhs, rhs } = expr;

  // Need exactly: variable, "=", and something on the RHS
  if (lhs?.length !== 1 || rhs.length === 0) return null;

  const [lhsNode] = lhs;
  if (!lhsNode.is(VariableNodeModel)) return null;

  // Decorator-aware name (e.g. i⃗ → `__dec_vector__i`) so it matches the
  // symbol keys the SymPy builder emits for decorated usages.
  const lhsName = getLabeledVariableName(lhsNode.toJSON());
  if (!lhsName) return null;

  if (hasNonEqualRelation(expr) || rhs.some(isEqualsSign)) return null;

  return {
    lhsNode,
    lhsName,
    rhsNodes: rhs,
    rhsJson: rhs.map((n) => substituteReferenceNodes(n.toJSON())),
  };
}

const isSetSymbolNode = (node: NodeModel): boolean =>
  node.is(VariableNodeModel) &&
  !!node.symbolNode?.props.key &&
  node.symbolNode.props.key in SET_SYMBOL_TO_SYMPY_MAP;

/**
 * Resolve a domain node (parsed by `tryParseDomain`) to a SymPy set expression.
 * Dispatches on node shape: an interval group becomes `sp.Interval(...)`, a
 * blackboard-bold set symbol (ℝ ℤ ℕ ℚ ℂ) becomes the matching `sp.S.*` set.
 * Returns null when the node is neither.
 */
export function domainNodeToSympy(node: NodeJson, variables: Map<string, string>): string | null {
  if (node.type === "group") return intervalGroupToSympy(node, variables);

  if (node.type === "variable" && node.children[0]?.type === "symbol") {
    const symbolKey = node.children[0].props.key;
    if (symbolKey && symbolKey in SET_SYMBOL_TO_SYMPY_MAP) {
      return semanticToSympyCode(
        { type: "operator", name: SET_SYMBOL_TO_SYMPY_MAP[symbolKey], args: [] },
        new Map(),
      );
    }
  }

  return null;
}

/**
 * Parse a line as `variable ∈ domain`, returning the variable name and the domain node.
 * The domain must be a single group node (an interval) or a set symbol (ℝ ℤ ℕ ℚ ℂ) —
 * a bare `x ∈ 5` is not a domain.
 */
export function tryParseDomain(expr: ScopeExpression): PriorDomain | null {
  const { lhs, rhs } = expr;
  if (lhs || rhs.length !== 3 || !rhs[0].is(VariableNodeModel)) return null;

  const variableName = rhs[0].symbolNode?.labeledValue;
  if (!variableName) return null;

  if (!rhs[1].is(OperatorSymbolNodeModel) || rhs[1].symbolNode?.props.key !== "symbol-element-of") {
    return null;
  }

  if (!rhs[2].is(GroupNodeModel) && !isSetSymbolNode(rhs[2])) return null;

  return { variableName, domainNode: rhs[2].toJSON(), wrapperId: expr.wrapperId };
}

export function collectPriorDomains(scope: ScopeExpression[]): Map<string, PriorDomain[]> {
  const domains = new Map<string, PriorDomain[]>();
  for (const expr of scope) {
    const domain = tryParseDomain(expr);
    if (!domain) continue;
    const existing = domains.get(domain.variableName);
    if (existing) {
      existing.push(domain);
    } else {
      domains.set(domain.variableName, [domain]);
    }
  }
  return domains;
}

/**
 * Resolve all domain declarations for a single variable into one SymPy set
 * string. Multiple declarations (e.g. `x ∈ (1,5)` and `x ∈ ℤ`) are combined
 * with `sp.Intersection`. Returns the combined SymPy string plus the wrapper
 * ids that contributed (for highlighting), or null if none resolved.
 */
export function resolveDomain(
  domains: PriorDomain[],
  variables: Map<string, string>,
): { sympy: string; wrapperIds: string[] } | null {
  const resolved = domains
    .map((d) => ({ sympy: domainNodeToSympy(d.domainNode, variables), wrapperId: d.wrapperId }))
    .filter((r): r is { sympy: string; wrapperId: string } => r.sympy !== null);

  if (resolved.length === 0) return null;

  const wrapperIds = resolved.map((r) => r.wrapperId);
  const sympy =
    resolved.length === 1
      ? resolved[0].sympy
      : `sp.Intersection(${resolved.map((r) => r.sympy).join(", ")})`;

  return { sympy, wrapperIds };
}

// Parse a line as `variable = expression`, skipping function definitions.
function tryParseAssignment(expr: ScopeExpression): PriorAssignment | null {
  const def = tryParseDefinition(expr);
  if (!def || def.lhsNode.hasParams) return null;

  // Skip self-referential assignments (e.g., x = x + 1)
  const rhsHasSelfRef = def.rhsNodes.some(
    (n) => n.is(VariableNodeModel) && getLabeledVariableName(n.toJSON()) === def.lhsName,
  );
  if (rhsHasSelfRef) return null;

  return { variableName: def.lhsName, rhsNodes: def.rhsJson, wrapperId: expr.wrapperId };
}

// Parse a line as a function definition like `f(x) = x^2 + 1`.
function tryParseFunctionDef(expr: ScopeExpression): PriorFunctionDef | null {
  const def = tryParseDefinition(expr);
  if (!def || !def.lhsNode.hasParams) return null;

  // Extract parameter names from the function's parameter list
  const paramNames: string[] = [];
  for (const param of def.lhsNode.parameters) {
    if (!param.is(InlineNodeModel)) return null;
    const paramNodes = param.nodes;
    if (paramNodes.length !== 1 || !paramNodes[0].is(VariableNodeModel)) return null;
    const paramName = paramNodes[0].symbolNode?.labeledValue;
    if (!paramName) return null;
    paramNames.push(paramName);
  }
  if (paramNames.length === 0) return null;

  // Skip recursive function definitions (e.g., f(x) = f(x) + 1)
  const rhsHasSelfRef = def.rhsNodes.some(
    (n) => n.is(VariableNodeModel) && n.hasParams && n.symbolNode?.labeledValue === def.lhsName,
  );
  if (rhsHasSelfRef) return null;

  return { funcName: def.lhsName, paramNames, rhsNodes: def.rhsJson, wrapperId: expr.wrapperId };
}

function collectPriorFunctionDefs(scope: ScopeExpression[]): PriorFunctionDef[] {
  const defMap = new Map<string, PriorFunctionDef>();

  for (const expr of scope) {
    const funcDef = tryParseFunctionDef(expr);
    if (funcDef) {
      defMap.set(funcDef.funcName, funcDef);
    }
  }

  return Array.from(defMap.values());
}

function mentionsVariable(expr: ScopeExpression, variables: Set<string>): boolean {
  return scopeExpressionNodes(expr).some((node) =>
    findDescendant(
      (n) => n.is(VariableNodeModel) && variables.has(n.symbolNode?.labeledValue ?? ""),
      node,
    ),
  );
}

/**
 * Equations in scope mentioning any of `variables`, searched from the end.
 * A single variable resolves to the most recent match only; several return every match.
 */
function findPrecedingEquasion(scope: ScopeExpression[], variables: string[]): ScopeExpression[] {
  const results: ScopeExpression[] = [];
  const variableSet = new Set(variables);

  for (let i = scope.length - 1; i >= 0; i--) {
    const expr = scope[i];
    if (!expr.lhs || hasNonEqualRelation(expr)) continue;
    if (!mentionsVariable(expr, variableSet)) continue;

    results.push(expr);
    if (variables.length === 1) return results;
  }

  return results;
}

/** Preceding lines with the target variable and exactly one relation (any kind). */
function findPrecedingRelational(scope: ScopeExpression[], variables: string[]): ScopeExpression[] {
  const results: ScopeExpression[] = [];
  const variableSet = new Set(variables);

  for (let i = scope.length - 1; i >= 0; i--) {
    const expr = scope[i];
    if (countRelations(expr) !== 1) continue;
    if (!mentionsVariable(expr, variableSet)) continue;

    results.push(expr);
    if (variables.length === 1) return results;
  }

  return results;
}

/**
 * Find the bracket style of the first matrix group in the input expression.
 */
function findInputMatrixBrackets(
  expression: NodeModel[],
): { opening: OpeningSymbol; closing: ClosingSymbol } | undefined {
  for (const node of expression) {
    const group = findDescendant((n) => {
      if (!n.is(GroupNodeModel)) return false;
      return n.childrenPlease.some((c) => c.is(MatrixNodeModel));
    }, node);
    if (group && group.is(GroupNodeModel)) {
      const opening = group.openingSymbol;
      const closing = group.closingSymbol;
      if (opening && closing) {
        return { opening, closing };
      }
    }
  }
  return undefined;
}

/**
 * Recursively apply the input matrix's bracket style to all matrix groups in the output.
 */
function applyMatrixBrackets(
  node: NodeJson,
  brackets: { opening: OpeningSymbol; closing: ClosingSymbol },
): void {
  if (
    node.type === "group" &&
    node.children.some((c) => c.type === "matrix" && (c.props.rows ?? 1) * (c.props.cols ?? 1) > 1)
  ) {
    node.props.openingSymbol = brackets.opening;
    node.props.closingSymbol = brackets.closing;
  }
  for (const child of node.children) {
    applyMatrixBrackets(child, brackets);
  }
}

/**
 * True when the cursor sits at the end of a line that contains only a single math expression and
 * nothing else (no prose text in the surrounding paragraph, no relation operator already present).
 * Used to decide whether to auto-show the idle "= result" phantom hint.
 */
export function isInlineMathOnlyLine(doc: DocumentModel): boolean {
  const { cursor } = doc;
  if (!cursor.isAtTheEndOfExpression || !cursor.node) return false;

  const lineNodes = cursor.node.nodes;
  if (lineNodes.length === 0) return false;

  // No expression-content prose text on the line.
  if (lineNodes.some((n) => n.is(PlainTextNodeModel))) return false;

  // No relation operator already present (=, <, >, <=, >=, ...). Defer to the typed-"=" flow.
  if (lineNodes.some(isRelation)) {
    return false;
  }

  // ProseMirror block gate: the inline expression must be the only meaningful content in its block.
  // (Model-level "parent is a WrapperNodeModel" is not enough — inline math can sit inside prose.)
  const { node: tiptapNode, position } = cursor.parentTiptapNode;
  if (!tiptapNode || position < 0) return false;

  const { tiptap } = doc.editor;
  if (!tiptap) return false;

  // A row header is served by the table-row hint watcher; letting the idle "=" flow fire there too
  // would show plain expression hints in place of the columnar row hints.
  if (isFormulaRowHeader(doc, tiptap)) return false;

  const $pos = tiptap.state.doc.resolve(position);
  const block = $pos.parent;
  let meaningfulSiblings = 0;
  block.forEach((child) => {
    if (nodeIsExpression(child)) {
      meaningfulSiblings += 1;
    } else if (child.isText) {
      if ((child.text ?? "").trim().length > 0) meaningfulSiblings += 1;
    } else {
      meaningfulSiblings += 1;
    }
  });
  return meaningfulSiblings === 1;
}

/**
 * A stable string fingerprint of the current inline-math editing position + content. Returns null
 * when the math-only gate fails. The content component makes the idle watcher recompute on
 * in-place inline-editable edits (node.value = value) that move neither the cursor nor emit a
 * node-creation tiptap onUpdate.
 */
export function inlineMathInputKey(doc: DocumentModel): string | null {
  if (!isInlineMathOnlyLine(doc)) return null;
  const { cursor } = doc;
  const wrapperId = cursor.parentWrapperNode?.id ?? "";
  const nodeId = cursor.node?.id ?? "";
  const index = cursor.index ?? -1;
  const content = nodeToExpressionText(
    { type: "inline", props: {}, children: (cursor.node?.nodes ?? []).map((n) => n.toJSON()) },
    { normalizeExp: true },
  );
  return `${wrapperId}|${nodeId}|${index}|${content}`;
}

/**
 * Idle-path hints: same hints as getPhantomHints, but each resolved result gets "=" prepended so
 * the ghost renders "= result" and the existing Tab/click accept inserts "= result" as one unit.
 * The typed-"=" path is left untouched (it does not call this).
 */
export function getInlineMathHints(doc: DocumentModel): PhantomHint[] {
  if (!isInlineMathOnlyLine(doc)) return [];

  const equalsNode = makeEqualsRelationJson();

  return getPhantomHints(doc).map((hint) => ({
    ...hint,
    getHint: async () => {
      const res = await hint.getHint();
      if (!res || res.equals || res.json.type !== "inline") return res;
      return { ...res, json: { ...res.json, children: [equalsNode, ...res.json.children] } };
    },
  }));
}
